import React, { useCallback, useRef, useEffect, useState } from 'react';
import { ContentUploaderProps } from '../types';
import { useContentUploader } from '../hooks/useContentUploader';
import UploadProgressBar from './UploadProgressBar';
import { LoadingStatus } from '@/types';

const ContentUploader: React.FC<ContentUploaderProps> = ({
  className = '',
  maxFiles = 10,
  acceptedFileTypes = ['image/*', 'video/*', 'audio/*'],
  maxFileSize = 100 * 1024 * 1024, // 100MB default
  onUploadError
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const {
    uploads,
    activeUploads,
    completedUploads,
    failedUploads,
    uploadFiles,
    cancelUpload,
    retryUpload,
    removeUploadFromQueue
  } = useContentUploader();

  // Handle file selection
  const handleFileSelect = useCallback((files: FileList | null) => {
    if (!files || files.length === 0) return;

    const fileArray = Array.from(files);
    
    // Validate file count
    if (fileArray.length > maxFiles) {
      onUploadError?.(`Maximum ${maxFiles} files allowed`);
      return;
    }

    // Validate file sizes and types
    const validFiles: File[] = [];
    const errors: string[] = [];

    fileArray.forEach(file => {
      // Check file size
      if (file.size > maxFileSize) {
        errors.push(`${file.name}: File size exceeds ${Math.round(maxFileSize / (1024 * 1024))}MB limit`);
        return;
      }

      // Check file type
      const isValidType = acceptedFileTypes.some(type => {
        if (type.endsWith('/*')) {
          const baseType = type.replace('/*', '');
          return file.type.startsWith(baseType);
        }
        return file.type === type;
      });

      if (!isValidType) {
        errors.push(`${file.name}: File type not supported`);
        return;
      }

      validFiles.push(file);
    });

    // Show errors if any
    if (errors.length > 0) {
      onUploadError?.(errors.join(', '));
    }

    // Upload valid files
    if (validFiles.length > 0) {
      uploadFiles(validFiles);
    }
  }, [maxFiles, maxFileSize, acceptedFileTypes, uploadFiles, onUploadError]);

  // Handle drag and drop
  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    handleFileSelect(e.dataTransfer.files);
  }, [handleFileSelect]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // Only set to false if we're leaving the window
    if (e.clientX === 0 && e.clientY === 0) {
      setIsDragOver(false);
    }
  }, []);

  // Handle file input change
  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    handleFileSelect(e.target.files);
    // Reset input value to allow selecting the same file again
    e.target.value = '';
  }, [handleFileSelect]);

  // Open file picker
  const openFilePicker = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  // Handle upload completion - currently unused but kept for future use
  // const handleUploadComplete = useCallback((uploadId: string) => {
  //   const upload = uploads.find(u => u.id === uploadId);
  //   if (upload?.content) {
  //     onUploadComplete?.(upload.content.id);
  //   }
  // }, [uploads, onUploadComplete]);

  // Handle retry
  const handleRetry = useCallback(async (uploadId: string) => {
    await retryUpload(uploadId);
  }, [retryUpload]);

  // Convert LoadingStatus to component status
  const getUploadStatus = (status: LoadingStatus): 'uploading' | 'completed' | 'failed' => {
    switch (status) {
      case LoadingStatus.LOADING:
        return 'uploading';
      case LoadingStatus.SUCCEEDED:
        return 'completed';
      case LoadingStatus.FAILED:
        return 'failed';
      default:
        return 'uploading';
    }
  };

  // Always render a minimal version for drag and drop, but only show full UI when there are uploads
  const hasUploads = uploads.length > 0;
  const [isDragOver, setIsDragOver] = useState(false);

  // Listen for global file picker trigger
  useEffect(() => {
    const handleTriggerFilePicker = () => {
      openFilePicker();
    };

    window.addEventListener('triggerFilePicker', handleTriggerFilePicker);
    return () => {
      window.removeEventListener('triggerFilePicker', handleTriggerFilePicker);
    };
  }, [openFilePicker]);

  return (
    <>
      {/* Global drag and drop area */}
      <div
        className={`fixed inset-0 z-40 pointer-events-none ${isDragOver ? 'pointer-events-auto' : ''}`}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
      />
      
      {/* Drag overlay indicator */}
      {isDragOver && (
        <div className="fixed inset-0 z-50 bg-blue-500 bg-opacity-20 flex items-center justify-center">
          <div className="bg-white rounded-lg p-8 shadow-lg border-2 border-dashed border-blue-500">
            <div className="text-center">
              <svg className="mx-auto h-12 w-12 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
              <p className="mt-4 text-lg font-medium text-gray-900">Drop files to upload</p>
              <p className="text-sm text-gray-500">
                {acceptedFileTypes.join(', ')} up to {Math.round(maxFileSize / (1024 * 1024))}MB
              </p>
            </div>
          </div>
        </div>
      )}
      
      {/* Hidden file input for programmatic access */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept={acceptedFileTypes.join(',')}
        onChange={handleInputChange}
        className="hidden"
      />

      {/* Upload progress UI - only show when there are uploads */}
      {hasUploads && (
        <div className={`fixed top-0 left-0 right-0 z-50 bg-white border-b border-gray-200 shadow-sm ${className}`}>
          {/* Upload progress list */}
          <div className="max-h-80 overflow-y-auto">
            <div className="p-2 space-y-2">
              {uploads.map(upload => (
                <UploadProgressBar
                  key={upload.id}
                  uploadId={upload.id}
                  fileName={upload.file.name}
                  progress={upload.progress}
                  status={getUploadStatus(upload.status)}
                  error={upload.error}
                  onCancel={upload.status === LoadingStatus.LOADING ? () => cancelUpload(upload.id) : undefined}
                  onRetry={upload.status === LoadingStatus.FAILED ? () => handleRetry(upload.id) : undefined}
                  onRemove={upload.status !== LoadingStatus.LOADING ? () => removeUploadFromQueue(upload.id) : undefined}
                />
              ))}
            </div>
          </div>

          {/* Summary bar */}
          <div className="px-4 py-2 bg-gray-50 border-t border-gray-200 flex justify-between items-center text-sm">
            <span className="text-gray-600">
              {activeUploads.length > 0 && `${activeUploads.length} uploading`}
              {completedUploads.length > 0 && `, ${completedUploads.length} completed`}
              {failedUploads.length > 0 && `, ${failedUploads.length} failed`}
            </span>
            <button
              onClick={() => uploads.forEach(u => removeUploadFromQueue(u.id))}
              className="text-gray-500 hover:text-gray-700 text-xs"
            >
              Clear all
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default ContentUploader; 